import "i18next";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import { getTraineeDetails } from "services/userServices";
import { Box, Grid, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { addHours, addMinutes, format, parseISO } from "date-fns";

const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  fontWeight: "bold",
  paddingBottom: theme.spacing(0.75),
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function TraineeDetails({ traineeOracleId, classTraineeId }) {
  const { t } = useTranslation(["TraineeDetails"], {
    useSuspense: false,
  });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: t("alert."),
      severity: "",
    });
  };
  const [classTraineeDetails, setClassTraineeDetails] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTraineeDetails, setSelectedTraineeDetails] = useState([]);
  const [value, setValue] = useState(0);
  const [expandedProjectIndex, setExpandedProjectIndex] = useState(null);
  const [expandedClientProgram, setExpandedClientProgram] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchTraineeDetails = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
          setClassTraineeDetails([]);
          setExpandedProjectIndex(null);
          setExpandedClientProgram(false);
        }

        if (traineeOracleId) {
          const traineeDetailsResponse =
            await getTraineeDetails(traineeOracleId);

          if (cancelled) return;

          if (traineeDetailsResponse?.message === "Success") {
            if (!cancelled) {
              setClassTraineeDetails(traineeDetailsResponse?.data);

              const index = traineeDetailsResponse?.data.findIndex(
                (trainee) => trainee.ClassTraineeId === classTraineeId,
              );

              if (index !== -1) {
                setSelectedTab(index);
                setSelectedTraineeDetails(traineeDetailsResponse?.data[index]);
              } else if (traineeDetailsResponse?.data[0]) {
                setSelectedTab(0);
                setSelectedTraineeDetails(traineeDetailsResponse?.data[0]);
              }
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setSnack({
            open: true,
            message: t(
              "alert.an_error_occurred_while_fetching_trainee_details_please_try_again_",
            ),
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTraineeDetails();

    return () => {
      cancelled = true;
    };
  }, [traineeOracleId, classTraineeId]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleTabChange = async (event, newValue) => {
    try {
      setLoading(true);
      setSelectedTab(newValue);
      setValue(0);
      setExpandedProjectIndex(null);
      setExpandedClientProgram(false);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const selectedTrainee = classTraineeDetails[newValue];
      if (selectedTrainee) {
        setSelectedTraineeDetails(selectedTrainee);
      }
    } catch {
      setSnack({
        open: true,
        message: t(
          "alert.an_error_occurred_while_changing_tabs_please_try_again_",
        ),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Grid container spacing={2}>
        {classTraineeDetails.length > 0 ? (
          <>
            <Grid item xs={2.5} alignItems="top">
              <SubHeading
                sx={{
                  fontSize: "16px",
                }}
              >
                {t("subheading.core_info")}
              </SubHeading>
              <Box
                sx={{
                  flexGrow: 1,
                  bgcolor: "background.paper",
                  display: "flex",
                  height: 750,
                }}
              >
                <Tabs
                  orientation="vertical"
                  variant="scrollable"
                  value={selectedTab}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    sx: {
                      backgroundColor: "#3299FF",
                      width: "5px",
                    },
                  }}
                  sx={{
                    borderRight: 1,
                    borderColor: "divider",
                    height: "100%",
                    overflowY: "auto",
                    width: "100%",
                    "& .MuiTabs-flexContainer": {
                      gap: 1,
                    },
                  }}
                >
                  {classTraineeDetails.map((trainee, index) => {
                    const isSelected = selectedTab === index;

                    return (
                      <Tab
                        key={trainee.ClassTraineeId}
                        label={
                          <Grid
                            container
                            spacing={1.5}
                            sx={{
                              py: 1.5,
                              px: 1,
                              textAlign: "left",
                              border: 2,
                              borderColor: isSelected ? "#3299FF" : "#E0E0E0",
                              borderRadius: 2,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                backgroundColor: "#F5F5F5", // ← Same hover effect for all cards
                                borderColor: isSelected ? "#3299FF" : "#BDBDBD",
                              },
                            }}
                          >
                            <Grid item xs={12}>
                              <SubHeading
                                sx={{
                                  color: isSelected ? "#1976D2" : "#757575",
                                }}
                              >
                                {t("subheading.wave")}
                              </SubHeading>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: "#3F5765",
                                  fontWeight: isSelected ? 600 : 400,
                                  fontSize: "14px",
                                }}
                              >
                                {trainee.Wave}
                              </Typography>
                            </Grid>

                            <Grid item xs={12}>
                              <SubHeading
                                sx={{
                                  color: isSelected ? "#1976D2" : "#757575",
                                }}
                              >
                                {t(
                                  "subheading.class_request_roster_trainee_id",
                                )}
                              </SubHeading>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: "#3F5765",
                                  fontWeight: isSelected ? 600 : 400,
                                  fontSize: "14px",
                                }}
                              >
                                {`${trainee.ClassRequestId} / ${trainee.ClassRosterId} / ${trainee.ClassTraineeId}`}
                              </Typography>
                            </Grid>

                            <Grid item xs={12}>
                              <SubHeading
                                sx={{
                                  color: isSelected ? "#1976D2" : "#757575",
                                }}
                              >
                                {t("subheading.trainee_status")}
                              </SubHeading>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: "#3F5765",
                                  fontWeight: isSelected ? 600 : 400,
                                  fontSize: "14px",
                                }}
                              >
                                {trainee.TraineeStatus}
                              </Typography>
                            </Grid>

                            <Grid item xs={12}>
                              <SubHeading
                                sx={{
                                  color: isSelected ? "#1976D2" : "#757575",
                                }}
                              >
                                {t("subheading.project")}
                              </SubHeading>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                sx={{
                                  color: "#3F5765",
                                  fontWeight: isSelected ? 600 : 400,
                                  fontSize: "14px",
                                }}
                              >
                                {trainee.ProjectName.split(",").length > 1 ? (
                                  <>
                                    {expandedProjectIndex === index ? (
                                      <>
                                        {trainee.ProjectName.split(",").map(
                                          (item, idx) => (
                                            <Typography
                                              key={`${idx}-${item}`}
                                              variant="subtitle1"
                                              sx={{
                                                color: "#3F5765",
                                                fontWeight: isSelected
                                                  ? 600
                                                  : 400,
                                                fontSize: "14px",
                                                mb: 0.5,
                                              }}
                                            >
                                              {item}
                                            </Typography>
                                          ),
                                        )}
                                        <IconButton
                                          size="small"
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3299FF",
                                            padding: 0,
                                            fontSize: "14px",
                                            textTransform: "none",
                                            "&:hover": {
                                              backgroundColor: "transparent",
                                              textDecoration: "underline",
                                            },
                                          }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setExpandedProjectIndex(null);
                                          }}
                                        >
                                          {t("iconbutton.all_projects")}
                                        </IconButton>
                                      </>
                                    ) : (
                                      <>
                                        {trainee.ProjectName.split(",")[0]}{" "}
                                        <IconButton
                                          size="small"
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3299FF",
                                            padding: 0,
                                            fontSize: "14px",
                                            minWidth: "auto",
                                            "&:hover": {
                                              backgroundColor: "transparent",
                                              textDecoration: "underline",
                                            },
                                          }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setExpandedProjectIndex(index);
                                          }}
                                        >
                                          {t("iconbutton._")}
                                          {trainee.ProjectName.split(",")
                                            .length - 1}
                                        </IconButton>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  trainee.ProjectName
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                        sx={{
                          textTransform: "none",
                          minHeight: "auto",
                          padding: 1.5, // ← Changed: Reduced horizontal padding
                          alignItems: "flex-start",
                          opacity: 1,
                          color: "#3F5765",
                          "&.Mui-selected": {
                            color: "#3F5765",
                            opacity: 1,
                          },
                          "&:not(.Mui-selected)": {
                            opacity: 1,
                          },
                          "&.MuiTab-root": {
                            opacity: 1,
                          },
                        }}
                      />
                    );
                  })}
                </Tabs>
              </Box>
            </Grid>
            <Grid item xs={9.5}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label={t("aria-label.simple_tabs_example")}
                TabIndicatorProps={{
                  sx: {
                    backgroundColor: "#3299FF",
                    height: "5px",
                  },
                }}
                sx={{
                  "& .MuiTab-root": {
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#3F5765",
                    letterSpacing: "0.10em",
                    textTransform: "none",
                    minWidth: "auto",
                    "&.Mui-selected": {
                      color: "#3299FF",
                    },
                    "&:hover": {
                      color: "#3299FF",
                    },
                  },
                }}
              >
                <Tab label={t("label.employee")} {...a11yProps(0)} />
                <Tab label={t("label.class_trainer")} {...a11yProps(1)} />
                <Tab label={t("label.tci")} {...a11yProps(2)} />
                <Tab label={t("label.product_training")} {...a11yProps(3)} />
                <Tab label={t("label.ttf")} {...a11yProps(4)} />
                <Tab label={t("label.ttd")} {...a11yProps(5)} />
              </Tabs>

              <TabPanel value={value} index={0}>
                <Grid container spacing={2} direction="row">
                  {/* First Column */}
                  <Grid item xs={3}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.candidate_id")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.CandidateId || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.oracle_username")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.ttec_email_id")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TTecEmail}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.first_name")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.FirstName}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.middle_name")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.last_name")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.LastName}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.date_of_birth")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.internation_id_ssn_")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.email_address")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.home_phone")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.mobile_phone")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Second Column */}
                  <Grid item xs={3}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.address_1")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.address_2")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.city_of_residency")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.state_province")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.zip_post_code")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.country_code")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.site")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.location_id")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Third Column */}
                  <Grid item xs={3}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.client_program")}
                        </SubHeading>
                        <Typography variant="subtitle1" gutterBottom>
                          {selectedTraineeDetails.ClientProgram &&
                          selectedTraineeDetails.ClientProgram.split(",")
                            .length > 1 ? (
                            <>
                              {expandedClientProgram ? (
                                <>
                                  {selectedTraineeDetails.ClientProgram.split(
                                    ",",
                                  ).map((item, idx) => (
                                    <Grid item key={`${idx}-${item}`}>
                                      <Typography variant="subtitle1">
                                        {item}
                                      </Typography>
                                    </Grid>
                                  ))}

                                  <IconButton
                                    sx={{
                                      cursor: "pointer",
                                      color: "#3299FF",
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      font: "inherit",
                                    }}
                                    onClick={(event) => {
                                      setExpandedClientProgram(false);
                                      event.stopPropagation();
                                    }}
                                  >
                                    {t("iconbutton.all_client_program")}
                                  </IconButton>
                                </>
                              ) : (
                                <>
                                  {
                                    selectedTraineeDetails.ClientProgram.split(
                                      ",",
                                    )[0]
                                  }{" "}
                                  <IconButton
                                    sx={{
                                      cursor: "pointer",
                                      color: "#3299FF",
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      font: "inherit",
                                    }}
                                    onClick={(event) => {
                                      setExpandedClientProgram(true);
                                      event.stopPropagation();
                                    }}
                                  >
                                    {t("iconbutton._")}
                                    {selectedTraineeDetails.ClientProgram.split(
                                      ",",
                                    ).length - 1}
                                  </IconButton>
                                </>
                              )}
                            </>
                          ) : (
                            selectedTraineeDetails.ClientProgram ||
                            "No Client Program Available"
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.department")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.department_code")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.job_title")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.bilingual")}</SubHeading>
                        <Typography variant="subtitle1">
                          {t("typography.na")}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Fourth Column */}
                  <Grid item xs={3}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.start_time")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.StartTime || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.stop_time")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.StopTime || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.current_state")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.CurrentState || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.current_step")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.CurrentStep || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.pref_first_name")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.FirstName}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.pref_last_name")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.LastName}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.tech_equipment_solution")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechEquipmentSolution || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.ort_status")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ORTStatus || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.ert_due_date_time")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ERTDueDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.ERTDueDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.ERTDueTime
                                    ? selectedTraineeDetails.ERTDueTime.split(
                                        ":",
                                      )
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.additional_comments")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AdditionalComments || "NA"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Class Trainer Tab */}
              <TabPanel value={value} index={1}>
                <Grid container spacing={2} direction="row">
                  <Grid item xs={4}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.wave")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.Wave || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.reschedule_transfer_details")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.RescheduleTransferDetails ||
                            "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.rescheduled_moved_to_new_roster")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.RescheduledMovedToNewRoster ||
                            "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.training_space")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TrainingSpace || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.attended_nesting")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedNesting || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.nesting_status")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.NestingStatus || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.nesting_date_time")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.NestingDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.NestingDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.NestingTime
                                    ? selectedTraineeDetails.NestingTime.split(
                                        ":",
                                      )
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.nesting_end_date_time")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.NestingEndDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.NestingEndDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.NestingEndTime
                                    ? selectedTraineeDetails.NestingEndTime.split(
                                        ":",
                                      )
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.attended_production")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedProduction || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.production_status")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ProductionStatus || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.production_date")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ProductionDate
                            ? format(
                                parseISO(selectedTraineeDetails.ProductionDate),
                                "MM/dd/yyyy",
                              )
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={4}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.trainee_status")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TraineeStatus || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.trainer_name")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TrainerName !== " " &&
                          selectedTraineeDetails.TrainerName
                            ? selectedTraineeDetails.TrainerName
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.trainer_email")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TrainerEmail || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.og_name")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.OGName !== " " &&
                          selectedTraineeDetails.OGName
                            ? selectedTraineeDetails.OGName
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.og_email")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.OGEmail || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.og_team_email")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.OGTeamEmail || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.operational_team_lead")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.OperationalTeamLead !== " " &&
                          selectedTraineeDetails.OperationalTeamLead
                            ? selectedTraineeDetails.OperationalTeamLead
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.operational_team_lead_email")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.OperationalTeamLeadEmail ||
                            "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.pre_training_comments")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.PretrainingComment || "NA"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* TCI Tab */}
              <TabPanel value={value} index={2}>
                <Grid container spacing={2} direction="row">
                  <Grid item xs={4}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.attended_tci")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedTCI || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.tci_disposition")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TCIDisposition || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.asd_lead")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ASDLead || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.build_rebuild_stick")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.BuildRebuildStick || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.tci_zoom_link")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TCIZoomLink || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.tci_zoom_password")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TCIZoomPassword || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.tci_date_time")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TCIDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.TCIDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.TCITime
                                    ? selectedTraineeDetails.TCITime.split(":")
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.perr_ext_reason")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.PERRExtReason || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.perr_ext_assigned")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.PERRExtAssigned || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.attended_tci_recovery")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedTCIRecovery || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.tci_recovery_date_time")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TCIRecoveryDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.TCIRecoveryDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.TCIRecoveryTime
                                    ? selectedTraineeDetails.TCIRecoveryTime.split(
                                        ":",
                                      )
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.tci_recovery_disposition")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TCIRecoveryDisposition ||
                            "NA"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Product Training Tab */}
              <TabPanel value={value} index={3}>
                <Grid container spacing={2} direction="row">
                  <Grid item xs={4}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.product_training_satus")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ProductTrainingStatus || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.attended_product_training")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedProductTraining ||
                            "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.product_training_date_time")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ProductTrainingDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.ProductTrainingDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.ProductTrainingTime
                                    ? selectedTraineeDetails.ProductTrainingTime.split(
                                        ":",
                                      )
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.product_training_zoom_link")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ProductTrainingZoomLink ||
                            "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.product_training_zoom_password")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.ProductTrainingZoomPassword ||
                            "NA"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* TTF Tab */}
              <TabPanel value={value} index={4}>
                <Grid container spacing={2} direction="row">
                  <Grid item xs={4}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.ttf_satus")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TTFStatus || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.attended_ttf")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedTTF || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.ttf_training_date_time")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TTFDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.TTFDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.TTFTime
                                    ? selectedTraineeDetails.TTFTime.split(":")
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.ttf_zoom_link")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TTFZoomLink || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.ttf_zoom_password")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TTFZoomPassword || "NA"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* TTD Tab */}
              <TabPanel value={value} index={5}>
                <Grid container spacing={2} direction="row">
                  <Grid item xs={4}>
                    <Grid container spacing={2} direction="column">
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.attended_ttd")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedTechTest || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.ttd_type")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestType || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.ttd_disposition")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestDisposition || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.failure_reason")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestFailureReason || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.ttd_date_time")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.TechTestDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.TechTestTime
                                    ? selectedTraineeDetails.TechTestTime.split(
                                        ":",
                                      )
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>{t("subheading.ttd_zoom_link")}</SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestZoomLink || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.ttd_zoom_password")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestZoomPassword || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.attended_ttd_recovery")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.AttendedTechTestRecovery ||
                            "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.ttd_recovery_date_time")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestRecoveryDate
                            ? (() => {
                                const date = parseISO(
                                  selectedTraineeDetails.TechTestRecoveryDate,
                                );
                                const [hours, minutes] =
                                  selectedTraineeDetails.TechTestRecoveryTime
                                    ? selectedTraineeDetails.TechTestRecoveryTime.split(
                                        ":",
                                      )
                                    : [0, 0];
                                const updatedDate = addMinutes(
                                  addHours(date, +hours),
                                  +minutes,
                                );
                                return format(
                                  updatedDate,
                                  "MM/dd/yyyy hh:mm a",
                                );
                              })()
                            : t("typography.na")}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <SubHeading>
                          {t("subheading.ttd_recovery_disposition")}
                        </SubHeading>
                        <Typography variant="subtitle1">
                          {selectedTraineeDetails.TechTestRecoveryDisposition ||
                            "NA"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                width: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#3F5765",
                  fontWeight: "bold",
                  letterSpacing: "0.10em",
                  padding: 2,
                }}
              >
                {t("typography.no_class_trainee_details_found_")}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
}

TraineeDetails.propTypes = {
  traineeOracleId: PropTypes.number,
  classTraineeId: PropTypes.number,
};
